import { axiosInstance } from "../../utils/axios";
import {
  CoObject,
  CoObjectDocumentPartInclude,
  CoObjectDropdown,
  CoObjectTypeEnum,
  ElaborationAbortRequest,
  TgkAmount,
  TgkAmountElaborationRequest,
  TgkAmountElaborationResponse,
  TgkDimensionAttributeElaborationRequest,
  TgkDimensionAttributeElaborationResponse,
  TgkTable,
  TgkTableElaborationRequest,
  TgkTableElaborationResponse,
  TgkTextElaborationRequest,
  TgkTextElaborationResponse,
} from "../models";
import { SourceListItem } from "../../components/interfaces/SourceListItemObj";
import { TgkDimensionAttribute } from "../models/tgk-dimension-attribute";
import { TgkText } from "../models/tgk-text";
import { TrackedObjChange } from "../../components/interfaces/ChangesTracker";
import { DocumentInsertionPoint } from "../../office/WordDocumentPart";
import { TgkAmountAihElaborationRequest } from "../models/tgk-amount-aih-elaboration-request";
import { DeleteCoObjectsResult } from "../models/delete-co-objects-result";

const BASE_PATH: string = "/coobject";
const BASE_PATH_CHANGES_TRACKER: string = "/tracking";

/*
 ******* TABLE **********
 */
export function startElaborationForTgkTableInsert(
  request: TgkTableElaborationRequest
): Promise<TgkTableElaborationResponse> {
  return axiosInstance
    .post(`${BASE_PATH}/table/add`, request, {
      params: { generatesElaboration: true },
    })
    .then((resp) => resp.data);
}

export function editTgkTable(
  request: TgkTableElaborationRequest,
  isSimpleEdit: boolean
): Promise<TgkTableElaborationResponse> {
  const params = { isSimpleEdit, generatesElaboration: true };
  return axiosInstance
    .put(`${BASE_PATH}/table/edit`, request, { params })
    .then((resp) => resp.data);
}

export function getTgkTableByLogicId(logicId: string): Promise<TgkTable> {
  return axiosInstance.get(`${BASE_PATH}/table/by-logic-id/${logicId}`).then((resp) => resp.data);
}

/*
 ******* AMOUNT **********
 */
export function startElaborationForTgkAmountInsert(
  request: TgkAmountElaborationRequest
): Promise<TgkAmountElaborationResponse> {
  return axiosInstance
    .post(`${BASE_PATH}/amount/add`, request, {
      params: { generatesElaboration: true },
    })
    .then((resp) => resp.data);
}

export function editTgkAmount(
  request: TgkAmountElaborationRequest,
  isSimpleEdit: boolean
): Promise<TgkAmountElaborationResponse> {
  const params = { isSimpleEdit, generatesElaboration: true };
  return axiosInstance
    .put(`${BASE_PATH}/amount/edit`, request, { params })
    .then((resp) => resp.data);
}

export function getTgkAmountByLogicId(logicId: string): Promise<TgkAmount> {
  return axiosInstance
    .get(`${BASE_PATH}/amount-or-aih/by-logic-id/${logicId}`)
    .then((resp) => resp.data);
}

/*
 ******* AMOUNT AIH **********
 */

export function startElaborationForTgkAmountAihInsert(
  request: TgkAmountAihElaborationRequest
): Promise<TgkAmountElaborationResponse> {
  return axiosInstance
    .post(`${BASE_PATH}/amountaih/add`, request, {
      params: { generatesElaboration: true },
    })
    .then((resp) => resp.data);
}

export function editTgkAmountAih(
  request: TgkAmountAihElaborationRequest,
  isSimpleEdit: boolean
): Promise<TgkAmountElaborationResponse> {
  const params = { isSimpleEdit, generatesElaboration: true };
  return axiosInstance
    .put(`${BASE_PATH}/amountaih/edit`, request, { params })
    .then((resp) => resp.data);
}

/*
 ******* DIMENSION ATTRIBUTE **********\
 */
export function startElaborationForTgkDimensionAttributeInsert(
  request: TgkDimensionAttributeElaborationRequest
): Promise<TgkDimensionAttributeElaborationResponse> {
  return axiosInstance
    .post(`${BASE_PATH}/dimensionattribute/add`, request, {
      params: { generatesElaboration: true },
    })
    .then((resp) => resp.data);
}

export function editTgkDimensionAttribute(
  request: TgkDimensionAttributeElaborationRequest,
  isSimpleEdit: boolean
): Promise<TgkDimensionAttributeElaborationResponse> {
  const params = { isSimpleEdit, generatesElaboration: true };
  return axiosInstance
    .put(`${BASE_PATH}/dimensionattribute/edit`, request, { params })
    .then((resp) => resp.data);
}

export function getTgkDimensionAttributeByLogicId(logicId: string): Promise<TgkDimensionAttribute> {
  return axiosInstance
    .get(`${BASE_PATH}/dimensionattribute/by-logic-id/${logicId}`)
    .then((resp) => resp.data);
}

/*
 ******* CHECKBOX **********
 */
export function checkboxInsert(request: CoObject): Promise<string> {
  return axiosInstance.post(`${BASE_PATH}/checkbox/add`, request).then((resp) => resp.data);
}

export function checkboxEdit(request: CoObject): Promise<string> {
  return axiosInstance.put(`${BASE_PATH}/checkbox/edit`, request).then((resp) => resp.data);
}

/*
 ******* DROPDOWN **********
 */
export function dropdownInsert(request: CoObjectDropdown): Promise<string> {
  return axiosInstance.post(`${BASE_PATH}/dropdown/add`, request).then((resp) => resp.data);
}

export function dropdownEdit(request: CoObjectDropdown): Promise<string> {
  return axiosInstance.put(`${BASE_PATH}/dropdown/edit`, request).then((resp) => resp.data);
}

/*
 ******* FREE_TEXT **********
 */
export function freeTextInsert(request: CoObject): Promise<string> {
  return axiosInstance.post(`${BASE_PATH}/freetext/add`, request).then((resp) => resp.data);
}

export function freeTextEdit(request: CoObject): Promise<string> {
  return axiosInstance.put(`${BASE_PATH}/freetext/edit`, request).then((resp) => resp.data);
}

/*
 ******* TGK_TEXT **********
 */
export function startElaborationForTgkTextInsert(
  request: TgkTextElaborationRequest
): Promise<TgkTextElaborationResponse> {
  return axiosInstance
    .post(`${BASE_PATH}/tgktext/add`, request, {
      params: { generatesElaboration: true },
    })
    .then((resp) => resp.data);
}

export function editTgkText(
  request: TgkTextElaborationRequest,
  isSimpleEdit: boolean
): Promise<TgkTextElaborationResponse> {
  const params = { isSimpleEdit, generatesElaboration: true };
  return axiosInstance
    .put(`${BASE_PATH}/tgktext/edit`, request, { params })
    .then((resp) => resp.data);
}

export function getTgkTextByLogicId(logicId: string): Promise<TgkText> {
  return axiosInstance.get(`${BASE_PATH}/tgktext/by-logic-id/${logicId}`).then((resp) => resp.data);
}

/*
 ******* DOCUMENT_PART **********
 */
export function documentPartInsert(
  request: CoObject,
  documentInsertionPoint: DocumentInsertionPoint
): Promise<CoObjectDocumentPartInclude> {
  const params = {
    ...(documentInsertionPoint
      ? { referenceDocumentId: documentInsertionPoint.referenceDocumentId }
      : {}),
    ...(documentInsertionPoint ? { position: documentInsertionPoint.position } : {}),
    generatesElaboration: true,
  };

  return axiosInstance
    .post(`${BASE_PATH}/documentpartinclude/add`, request, { params })
    .then((resp) => resp.data);
}

export function documentPartLink(
  request: CoObject,
  documentInsertionPoint: DocumentInsertionPoint
): Promise<CoObjectDocumentPartInclude> {
  const params = {
    ...(documentInsertionPoint
      ? { referenceDocumentId: documentInsertionPoint.referenceDocumentId }
      : {}),
    ...(documentInsertionPoint ? { position: documentInsertionPoint.position } : {}),
    generatesElaboration: true,
  };

  return axiosInstance
    .post(`${BASE_PATH}/documentpartinclude/link`, request, { params })
    .then((resp) => resp.data);
}

export function documentPartNonTagetik(
  request: CoObject,
  driveItemId: string,
  documentInsertionPoint: DocumentInsertionPoint
): Promise<CoObjectDocumentPartInclude> {
  const params = {
    ...(documentInsertionPoint
      ? { referenceDocumentId: documentInsertionPoint.referenceDocumentId }
      : {}),
    ...(documentInsertionPoint ? { position: documentInsertionPoint.position } : {}),
    generatesElaboration: true,
  };

  return axiosInstance
    .post(`${BASE_PATH}/documentpartinclude/linknontagetik/${driveItemId}`, request, { params })
    .then((resp) => resp.data);
}

/**
 * Links an existing content control to a document part by creating a DOCUMENT_PART_INCLUDE object
 * Used by the troubleshooting tool to fix broken or missing links
 * @param documentId The document ID
 * @param contentControlId The content control ID to link
 * @param includedDocumentPartId The document part ID to link to
 * @param objectName Optional name for the CoObject
 */
export function troubleshootingFixLinkDocumentPart(
  documentId: string,
  contentControlId: number,
  includedDocumentPartId: string,
  objectName?: string
): Promise<CoObjectDocumentPartInclude> {
  const request: CoObjectDocumentPartInclude = {
    documentId,
    contentControlId,
    includedDocumentPartId,
    name: objectName || "Document Part",
    type: CoObjectTypeEnum.DOCUMENT_PART_INCLUDE,
  };

  return axiosInstance
    .post(`${BASE_PATH}/documentpartinclude/troubleshooting-fix-link`, request)
    .then((resp) => resp.data);
}

/*
 ********* LIST COOBJECT **********
 */

export function listDocumentCoObjects(
  documentId: string,
  language: string
): Promise<SourceListItem[]> {
  const params = { language };
  return axiosInstance.get(`${BASE_PATH}/${documentId}`, { params }).then((resp) => resp.data);
}

export function listAllDocumentCoObjects(
  documentId: string,
  language: string
): Promise<SourceListItem[]> {
  const params = { language };
  return axiosInstance
    .get(`${BASE_PATH}/recursive/${documentId}`, { params })
    .then((resp) => resp.data);
}

/*
 ******* DELETE COOBJECT **********
 */

export async function coObjectDelete(
  coObjects: string | string[]
): Promise<boolean | DeleteCoObjectsResult[]> {
  if (!Array.isArray(coObjects)) {
    coObjects = [coObjects];
  }
  if (!coObjects.length) return;
  if (coObjects.length === 1) {
    return axiosInstance.delete(`${BASE_PATH}/delete/${coObjects[0]}`).then((resp) => resp.data);
  }
  return axiosInstance.post(`${BASE_PATH}/delete`, coObjects).then((resp) => resp.data);
}

export function abortElaboration(elaborationRequest: ElaborationAbortRequest): Promise<string> {
  return axiosInstance
    .put(`/elaboration/abort`, elaborationRequest, {
      params: { generatesElaboration: true },
    })
    .then((response) => response.data);
}

export function getLogicId(documentId: string, contentControlId: number): Promise<string> {
  return axiosInstance
    .get(`${BASE_PATH_CHANGES_TRACKER}/logicId/${documentId}/${contentControlId}`)
    .then((response) => response.data);
}

export function getTrackedChangesForCoObject(objectId: string): Promise<TrackedObjChange[]> {
  return axiosInstance
    .get(`${BASE_PATH_CHANGES_TRACKER}/${objectId}`)
    .then((response) => response.data);
}

export function exportObjects(documentId: string): Promise<ArrayBuffer> {
  return axiosInstance
    .post(`${BASE_PATH}/${documentId}/exportDocumentObjectList`, [], {
      responseType: "arraybuffer",
    })
    .then((resp) => resp.data);
}
